import { Link } from 'react-router-dom';
import { Linkedin, Youtube } from 'lucide-react';

function TikTokIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.71a8.28 8.28 0 0 0 4.84 1.54V6.8a4.84 4.84 0 0 1-1.07-.11z" />
    </svg>
  );
}

type SocialLink = {
  href: string;
  label: string;
  icon: React.ReactNode;
};

const yousifSocials: SocialLink[] = [
  {
    href: 'https://www.linkedin.com/in/yussef-idris-94644a373/',
    label: 'Yousif on LinkedIn',
    icon: <Linkedin size={16} />,
  },
  {
    href: 'https://www.youtube.com/@aicrossfields',
    label: 'AI Crossfields on YouTube',
    icon: <Youtube size={16} />,
  },
  {
    href: 'https://www.tiktok.com/@yousif.aisystems',
    label: 'Yousif on TikTok',
    icon: <TikTokIcon size={16} />,
  },
];

function SocialRow({ name, links }: { name: string; links: SocialLink[] }) {
  return (
    <div className="flex items-center gap-3">
      <span className="w-16 font-mono text-[0.6rem] uppercase tracking-label text-stone">
        {name}
      </span>
      <div className="flex items-center gap-2">
        {links.map((l) => (
          <a
            key={l.href}
            href={l.href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={l.label}
            className="text-stone transition-colors hover:text-gold"
          >
            {l.icon}
          </a>
        ))}
      </div>
    </div>
  );
}

export default function Footer() {
  return (
    <footer className="grain border-t border-white/5 bg-black">
      <div className="container-x py-16">
        <div className="grid gap-12 md:grid-cols-[1.5fr_1fr_1fr_1fr]">
          <div>
            <Link to="/" className="font-display text-2xl text-white">
              Samia Systems<span className="text-gold">.</span>
            </Link>
            <p className="mt-4 max-w-sm font-sans text-body text-white/80">
              Engineering-grade AI automation for professional services firms. Every
              engagement begins with a Technical Blueprint.
            </p>
            <div className="mt-6 inline-flex items-center gap-2">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-pulse-gold rounded-full bg-gold opacity-60" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-gold" />
              </span>
              <span className="font-mono text-[0.65rem] uppercase tracking-label text-stone">
                System Operational
              </span>
            </div>

            <div className="mt-8 flex flex-col gap-3">
              <SocialRow name="Yousif" links={yousifSocials} />
              </div>
          </div>

          <FooterCol
            title="Practice"
            links={[
              { to: '/services', label: 'Services' },
              { to: '/methodology', label: 'Methodology' },
              { to: '/introductions', label: 'Introductions' },
              { to: '/blog', label: 'Blog' },
            ]}
          />
          <FooterCol
            title="Resources"
            links={[
              { to: '/library', label: 'Library' },
              { to: '/about', label: 'About' },
              { to: '/contact', label: 'Contact' },
            ]}
          />
          <FooterCol
            title="Engage"
            links={[
              { to: '/book', label: 'Book a Diagnostic' },
              { to: '/privacy', label: 'Privacy' },
              { to: '/terms', label: 'Terms' },
            ]}
          />
        </div>

        <div className="mt-16 flex flex-col items-start justify-between gap-3 border-t border-white/5 pt-6 md:flex-row md:items-center">
          <p className="font-sans text-caption text-stone">
            © {new Date().getFullYear()} Samia Systems Limited. United Kingdom.
          </p>
          <p className="font-mono text-[0.65rem] uppercase tracking-label text-stone">
            Intelligence. Engineered.
          </p>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({
  title,
  links,
}: {
  title: string;
  links: { to: string; label: string }[];
}) {
  return (
    <div>
      <p className="label mb-4">{title}</p>
      <ul className="flex flex-col gap-2">
        {links.map((l) => (
          <li key={l.to}>
            <Link
              to={l.to}
              className="font-sans text-sm text-white/85 transition-colors hover:text-gold"
            >
              {l.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
